import { useState, useEffect, useCallback } from "react";
import { usePetContext } from "@/context/PetContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAdminCheck } from "./useAdminCheck";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { logger } from "@/utils/logger";
import { setPaddleRetainCustomer } from "@/lib/paddle";

export const FREE_PETS_LIMIT = 2;

export const useSubscription = () => {
  const { pets } = usePetContext();
  const { isAdmin } = useAdminCheck();
  const { user } = useAuth();
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isBreeder, setIsBreeder] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fonction pour vérifier l'abonnement depuis la base de données
  const checkSubscriptionFromDB = useCallback(async () => {
    if (!user) {
      setIsSubscribed(false);
      setIsBreeder(false);
      setLoading(false);
      return;
    }

    try {
      // Les admins ont accès complet
      if (isAdmin) {
        logger.debug(
          "👑 useSubscription - Admin détecté, accès complet accordé",
        );
        setIsSubscribed(true);
        setIsBreeder(true);
        setNeedsSubscription(false);
        setLoading(false);
        return;
      }

      // Utilise la vue sécurisée my_subscription qui exclut stripe_customer_id et stripe_subscription_id
      const { data: subscription, error } = await supabase
        .from("my_subscription")
        .select("*")
        // 'trial' = essai en cours → l'utilisateur DOIT avoir les fonctions du plan
        .in("status", ["active", "trial"])
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        logger.error(
          "❌ Erreur lors de la récupération de l'abonnement:",
          error,
        );
        setLoading(false);
        return;
      }

      if (subscription) {
        // Vérifier si l'abonnement n'est pas expiré
        const isActive =
          !subscription.expires_at ||
          new Date(subscription.expires_at) > new Date();

        setIsSubscribed(isActive);
        setIsBreeder(subscription.plan_type === "breeder" && isActive);
        setSubscriptionPlan(subscription.plan_type);
        setSubscriptionEndDate(subscription.expires_at);

        // Paddle Retain : lie la session au client Paddle (fire-and-forget).
        // Cast : la colonne vient d'être ajoutée à la vue, types pas régénérés.
        const paddleCustomerId = (
          subscription as { paddle_customer_id?: string | null }
        ).paddle_customer_id;
        if (isActive && paddleCustomerId) {
          void setPaddleRetainCustomer(paddleCustomerId);
        }

        logger.debug("✅ useSubscription - Abonnement actif:", {
          plan: subscription.plan_type,
          expires_at: subscription.expires_at,
        });
      } else {
        setIsSubscribed(false);
        setIsBreeder(false);
        setSubscriptionPlan(null);
        setSubscriptionEndDate(null);
        logger.debug("ℹ️ useSubscription - Aucun abonnement actif");
      }
    } catch (error) {
      logger.error("❌ Erreur lors de la vérification de l'abonnement:", error);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  useEffect(() => {
    checkSubscriptionFromDB();
  }, [checkSubscriptionFromDB]);

  // Vérifier si l'utilisateur dépasse la limite gratuite
  useEffect(() => {
    if (loading) return;

    if (
      pets.length >= FREE_PETS_LIMIT &&
      !isSubscribed &&
      !isBreeder &&
      !isAdmin
    ) {
      setNeedsSubscription(true);
    } else {
      setNeedsSubscription(false);
    }
  }, [pets.length, isSubscribed, isBreeder, isAdmin, loading]);

  const checkSubscriptionBeforeAction = useCallback(
    (action: () => void) => {
      // Les admins peuvent toujours effectuer l'action
      if (isAdmin) {
        logger.debug("👑 useSubscription - Admin bypass pour action");
        action();
        return;
      }

      if (needsSubscription && !isSubscribed && !isBreeder) {
        toast.error(
          `Vous avez dépassé la limite de ${FREE_PETS_LIMIT} animaux gratuits`,
          {
            description: "Veuillez vous abonner pour continuer",
            action: {
              label: "S'abonner",
              onClick: () => navigate("/subscription"),
            },
          },
        );
      } else {
        action();
      }
    },
    [isAdmin, needsSubscription, isSubscribed, isBreeder, navigate],
  );

  // Recharger l'abonnement depuis la base de données
  const refreshSubscription = async () => {
    await checkSubscriptionFromDB();
  };

  const getSubscriptionPlan = useCallback(() => {
    return subscriptionPlan;
  }, [subscriptionPlan]);

  const getSubscriptionEndDate = useCallback(() => {
    return subscriptionEndDate ? new Date(subscriptionEndDate) : null;
  }, [subscriptionEndDate]);

  const getRemainingDays = useCallback(() => {
    if (!subscriptionEndDate) return null;

    const endDate = new Date(subscriptionEndDate);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0;
  }, [subscriptionEndDate]);

  return {
    isSubscribed,
    isBreeder,
    isAdmin,
    needsSubscription,
    subscriptionPlan,
    loading,
    checkSubscriptionBeforeAction,
    refreshSubscription,
    getSubscriptionPlan,
    getSubscriptionEndDate,
    getRemainingDays,
    FREE_PETS_LIMIT,
  };
};
